import type { UseFetchOptions } from "#app";
import { toValue } from "vue";
import type { WithRequired } from "~/types/types";

interface PageQueryVariableOptions {
  [key: string]: string | PageQueryVariableOptions;
}
interface UsePageQueryReturn<T> {
  data: T;
}

// One payload re-load per page at a time. A page's own query and the layout's
// footer query read from the same _payload.json, so they lose it together.
//
// Module-level state is safe here even though it would leak between requests
// on a server handling live traffic. This site is statically generated, so the
// only server-side run is the prerender build, where loadPayload resolves to
// null. Entries are keyed by page path and deleted as soon as they settle.
const payloadReloads = new Map<string, ReturnType<typeof loadPayload>>();

const reloadPayload = (path: string, nuxtApp: ReturnType<typeof useNuxtApp>) => {
  let reload = payloadReloads.get(path);
  if (!reload) {
    reload = loadPayload(path, { fresh: true })
      .then((payload) => {
        // Make the recovered data available to the page's later queries, as
        // Nuxt's own payload plugin does after a navigation. They run one
        // after another during hydration, so without this each would re-fetch.
        // With a payloadPath this merges another page's payload; the keys it
        // shares with the current page (header, footer) hold the same values.
        if (payload?.data) Object.assign(nuxtApp.static.data, payload.data);
        return payload;
      })
      // A safety net: loadPayload already resolves to null when the payload
      // fetch fails, and it no longer depends on the app manifest (disabled in
      // nuxt.config.ts). If it ever rejects, treat that as no payload: the
      // retry must never turn a page with missing data into a thrown error.
      .catch(() => null)
      .finally(() => payloadReloads.delete(path));
    payloadReloads.set(path, reload);
  }
  return reload;
};

/**
 *
 * @description test
 * @param variables
 * @param options
 */
export const usePageQuery = async <T>(
  query: string,
  variables: PageQueryVariableOptions = {},
  {
    payloadPath,
    ...options
  }: WithRequired<
    Omit<UseFetchOptions<UsePageQueryReturn<T>>, "method" | "headers" | "body" | "getCachedData">,
    "key"
  > & {
    // The page whose _payload.json holds this query's data, when that isn't
    // the current page. Defaults to the current route.
    payloadPath?: string;
  }
) => {
  // Capture the route before awaiting. After an `await` inside a composable
  // the component instance is gone and useRoute() falls back to the app-level
  // route, which during a client-side navigation is still the page being left.
  const route = useRoute();
  const nuxtApp = useNuxtApp();

  // backup data key, manually setting a key would be better but this makes sure we're keying by default
  const { data, error } = await useFetch<UsePageQueryReturn<T>, Error>("/api/graphql", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      query,
      variables
    }),
    deep: false,
    ...options,
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key];
    }
  });

  let result = data.value?.data;

  // Nuxt loads each prerendered page's data from its _payload.json with a
  // single fetch and no retry, so one dropped request leaves the page with no
  // data, and the static site has no /api/graphql to fall back on. Re-load the
  // payload once before treating the data as missing. loadPayload is a no-op
  // during prerender, and `fresh` cache-busts the request. See PDASUOW-25840.
  if (!result) {
    const payload = await reloadPayload(payloadPath ?? route.path, nuxtApp);
    result = payload?.data?.[toValue(options.key)]?.data;
  }

  // Report-only for now. Returning undefined here is what previously let pages
  // hand `undefined` to their templates; most page templates are fully
  // optional-chained and render a degraded shell rather than crashing, so
  // throwing would convert ~47 pages from degraded to a 500. Before making that
  // trade we need to know which keys actually fail in production, and whether
  // the request failed in transport or simply came back empty:
  //
  //   - "transport": nothing answered. On the statically generated site the
  //     client-side POST /api/graphql has no handler deployed, so a page
  //     whose prerendered payload failed to load, and whose one retry above
  //     also failed, lands here.
  //   - "empty-result": the server answered but returned no data envelope.
  //     Also the mode for a lost payload during hydration, when useFetch
  //     defers its request until mount and resolves with neither.
  //
  // Partial GraphQL failures (HTTP 200 with both `data` and `errors`) are
  // deliberately not reported: they leave a usable envelope and pages render
  // fine today. See PDASUOW-25579.
  //
  // No `import.meta.client` guard is needed: sentry.client.ts only provides
  // $sentryCaptureException in the browser, so the optional call no-ops during
  // prerender.
  if (!result) {
    const mode = error.value ? "transport" : "empty-result";
    const payloadNote = payloadPath ? `, payload: ${payloadPath}` : "";
    nuxtApp.$sentryCaptureException?.(
      new Error(`usePageQuery ${mode} failure (key: ${options.key}${payloadNote})`, {
        cause: error.value ?? undefined
      })
    );
  }

  return result;
};
